<template>
  <div class="gs-app gm-mail-categories-refresh gm-refresh-tables">
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>

    <div class="flex mb-4 mx-4">
      <div class="w-full relative gm-mail-categories-refresh__workspace">
        <v-wait for="mailCategories">


          <navigation-tabs
            class="gm-nav-category-tabs absolute"
            :nav_tabs="navTabs"
          ></navigation-tabs>

          <client-table v-if="!isLoading"
            class="groovemail-category-table gs-refresh-controls"
            :tableRef="'categoryTable'"
            :tableData="mailCategories"
            :tableColumns="mailCategoryTableColumns"
            :tableOptions="mailCategoryTableOptions"
            :tableTitle="'frontend_sites_template_categories'"
            :tableCondition="mailCategories && mailCategories.length"
            :slotData="slotData"
            :emptyStateData="emptyStateData"
            :addNewBtnText="'frontend_new_category'"
            @addNewData="openCreateCategory"
          >
          </client-table>

          <gs-named-modal
            name="category-modal"
            :title="
              currentMode === 1
                ? $t('frontend_new_category')
                : $t('frontend_groovemail_mail_category_upadate_cat')
            "
            @closed="modalClosed()"
          >
            <div class="gs-app p-6 gm-mail-categories-refresh__dialog gm-refresh-dialog">
              <div class="flex flex-col mb-6">
                <label class="font-semibold text-sm text-grey-700 mb-2">
                  {{ $t('frontend_groovemail_mail_tag_name') }}
                </label>
                <text-input
                  variant="base"
                  v-model="model.name"
                  :placeholder="$t('frontend_groovemail_adminsetting_name')"
                  :validator="$v.model.name"
                />
              </div>

              <div class="flex justify-end">
                <gs-button
                  variant="outline"
                  @click="closeCreateCategoryModal()"
                  >{{ $t('frontend_cancel') }}</gs-button>
                <gs-button
                  class="ml-2"
                  variant="primary"
                  @click="createMailCategory()"
                >
                  {{
                    currentMode === 1
                      ? $t('frontend_save')
                      : $t('frontend_update')
                  }}
                </gs-button>
              </div>
            </div>
          </gs-named-modal>
        </v-wait>
      </div>
    </div>


    <!-- Confirmation delete modal -->
    <confirm-modal
      :ref="`confirmation-delete-mail-cat`"
      :name="`confirmation-delete-mail-cat`"
      :buttonText="$t('frontend_confirm')"
      :cancelText="$t('frontend_cancel')"
      :confirmMessage="this.$t('frontend_bouses_delete_bonus_title')"
      :messageContent="this.$t('frontend_groovemail_swal_revert_message')"
      @confirm="deleteMailCategory"
      @close="closeDeleteCategoryModal"
    />
  </div>
</template>

<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import ClientTable from '@groovepages/gd-ui-kit/components/tables/ClientTable.vue';
import { categoryUrl } from '@/utils/urls.js';
import { getData, deleteData, postData, putData } from '@/utils/apiHelper.js';
import NavigationTabs from '@/components/navigation-tabs.vue';
import themeConfig from '@/theme/themeConfig.js';
import showAlert from '@/components/sweet-alert-popup.js';

export default {
  middleware: 'auth',
  metaInfo() {
    return { title: this.$t('frontend_sites_template_categories') };
  },

  data() {
    return {
      mailCategories: [],
      isLoading: false,
      model: {
        name: '',
      },
      currentMode: 0,
      currentCatId: '',
      emptyStateData: {
        emptyStateHeading: 'frontend_sites_template_categories',
        emptyStateSubHeading:
          'frontend_groovemail_mail_category_you_currently_dont_have',
        emptyStateBtnText: 'frontend_new_category',
      },
      navTabs: [
        {
          tabName: 'frontend_groovemail_components_pricepoint_sidebar_lists',
          tabRoute: 'Lists',
        },
        {
          tabName: 'frontend_groovemail_components_pricepoint_sidebar_tags',
          tabRoute: 'Tags',
        },
        {
          tabName: 'frontend_sites_template_categories',
          tabRoute: 'Category',
        },
        {
          tabName: 'frontend_groovemail_components_pricepoint_sidebar_segments',
          tabRoute: 'Segments',
        },
      ],
      mailCategoryTableColumns: ['name', 'tags_count', 'lead_count', 'actions'],
      mailCategoryTableOptions: {
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        sortable: ['name', 'tags_count', 'lead_count'],
        filterable: ['name'],
        perPage: 10,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        headings: {
          name: () => this.$t('frontend_groovemail_adminsetting_name'),
          tags_count: () => this.$t('frontend_groovemail_mail_lead_show_tags'),
          lead_count: () =>
            this.$t('frontend_groovemail_tags_category_listing_lead_tagged'),
          actions: () => this.$t('frontend_groovemail_mail_lead_actions'),
        },
        rowClassCallback: () => 'hover:bg-grey-50 funnelRow',
      },

      categoryId: null,
    };
  },

  validations: {
    model: {
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(50),
      },
    },
  },

  components: {
    NavigationTabs,
    ClientTable,
  },

  computed: {
    /**	
     * Returns slot data for categories table.
     * @return {array} details for slot columns for categories table.
    */
    slotData() {
      return [
        {
          slotName: 'name',
          slotType: 'default',
          showDescription: false,
          defaultVal: this.$t('frontend_groovemail_not_available'),
        },
        {
          slotName: 'tags_count',
          slotType: 'clickable',
          callback: (props) => this.showTagsData(props.row.id),
        },
        {
          slotName: 'lead_count',
          slotType: 'default',
          defaultVal: 0,
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          actionList: [
            {
              id: 0,
              name: 'frontend_edit',
              conditions: (row) => {
                return row.is_default !== 1;
              },
              callback: (props) => this.openEditMailCategory(props.row),
            },
            {
              id: 1,
              name: 'frontend_delete',
              conditions: (row) => {
                return !row.tags_count;
              },
              callback: (props) => this.deleteCategoryModal(props.row.id),
            },
          ],
        },
      ];
    },
  },

  // Calls fetchCategories method when page loads to fetch list of all categories.
  async created() {
    // show loader
    this.showLoader();

    await this.fetchCategories(true);

    // hide loader
    this.hideLoader();
  },

  methods: {
    // show loader
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    // hide loader
    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    // error message for the request field
    async requestErrorAlert(error) {
      // hide the loader
      this.hideLoader();
      
      await showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark mb-3">${error || this.$t('frontend_backend_error') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    // Resets name & currentCatId property to default value & removes error message if any when category-modal is closed.
    modalClosed() {
      this.model.name = '';
      this.currentCatId = '';
      this.$v.model.name.$reset();
    },

    /**
     * Opens tags page with category filter as query to display filtered tags.
     *  @param {string} catId category id choosen
     */
    showTagsData(catId) {
      this.$router.push({ name: 'Tags', query: { catId } });
    },

    // Makes an API call that lists details of all Categories.
    async fetchCategories(primaryLoading) {
      const categoryData = await getData(categoryUrl, null, primaryLoading);
      this.mailCategories = categoryData?.data;
    },

    // Intializes category data and triggers create-category modal to open
    openCreateCategory() {
      this.model.name = '';
      this.currentMode = 1;
      this.openCreateCategoryModal();
    },

    // Opens create-category modal
    openCreateCategoryModal() {
      this.$root.$emit('modal-open', 'category-modal');
    },

    // Closes create-category modal
    closeCreateCategoryModal() {
      this.$root.$emit('modal-close', 'category-modal');
    },

    /**
     * Creates or Edits mail category based on currentMode data property.
     * If currentMode = 1, create new mail category,
     *    currentMode = 2, edit existing mail category
     */
    async createMailCategory() {
      this.$v.model.name.$touch();
      if (!this.$v.model.name.$invalid) {
        // show loader
        this.showLoader();

        const categoryData = {
          name: this.model.name
        };
        try {
          if (this.currentMode === 1) {
            await postData(categoryUrl, categoryData).then(async () => {
              await this.fetchCategories();
              this.closeCreateCategoryModal();
            });
          } else {
            await putData(`${categoryUrl}/${this.currentCatId}`, categoryData).then(async () => {
              await this.fetchCategories();
              this.closeCreateCategoryModal();
            });
          }
          // hide loader
          this.hideLoader();
        }
        // Shows error dialog if any error occurs while creating or editing category.
        catch (error) {
          // show error alert
          this.requestErrorAlert(error.response?.data?.error);
        }
      }
    },

    /**
     * Opens create-category modal to a edit existing category.
     *  @param {object} category contains details of a category.
     */
    async openEditMailCategory(category) {
      this.model.name = category.name;
      this.currentMode = 2;
      this.currentCatId = category.id;
      this.openCreateCategoryModal();
    },

    // open delete category modal
    async deleteCategoryModal(id) {
      // set the category id
      this.categoryId = id;

      // open the modal
      this.$refs[`confirmation-delete-mail-cat`].open();
    },

    // close delete category modal
    closeDeleteCategoryModal() {
      // reset the category id
      this.categoryId = null;

      // close the modal
      this.$refs[`confirmation-delete-mail-cat`].close();
    },

    // delete category
    async deleteMailCategory() {      
      try {
        // delete category by id and fetch categories again
        await deleteData(`${categoryUrl}/${this.categoryId}`);
        await this.fetchCategories();
      } catch (error) {
        // show error alert
        this.requestErrorAlert(error.response?.data?.error);
      }
    },
  },
};
</script>

<style lang="scss">
.groovemail-category-table .gd-table {
  .VueTables--client table {
    th:nth-child(1),
    th:nth-child(1) {
      width: 20%;
    }
    td:nth-child(2),
    td:nth-child(2),
    td:nth-child(3),
    td:nth-child(3),
    th:nth-child(4),
    td:nth-child(4) {
      width: 15%;
    }
    th:nth-child(5),
    td:nth-child(5) {
      width: 15%;
    }
  }
}

.gm-nav-category-tabs {
  top: 25px;
  z-index: 10;
}
</style>

<style scoped>
.gm-mail-categories-refresh {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-mail-categories-refresh__workspace {
  min-width: 0;
  padding: 20px 24px 24px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

@media (max-width: 640px) {
  .gm-mail-categories-refresh {
    padding: 18px 12px 32px;
  }

  .gm-mail-categories-refresh__workspace {
    padding: 16px;
  }
}
</style>
